import React, { useEffect, useState } from 'react';
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Stack
} from '@mui/material';
import { CalendarToday, LocationOn } from '@mui/icons-material';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { Evento } from './Evento';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon
} from 'react-share';
import { AddToCalendarButton } from 'add-to-calendar-button-react';

const EventoDetalle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [evento, setEvento] = useState<Evento | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchEvento = async () => {
      try {
        const response = await axios.get<Evento>(`${config.backendUrl}/api/Eventos/${id}`);
        setEvento(response.data);
      } catch (error) {
        console.error('Error al obtener el evento:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvento();
  }, [id]);

  const formatDate = (dateString: Date | string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('es-AR', options);
  };

  const formatDateForGoogle = (date: Date | string | null) => {
    if (!date) return '';
    return new Date(date).toISOString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!evento) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h6">Evento no encontrado</Typography>
        <Button component={Link} to="/eventos" variant="outlined" sx={{ mt: 2 }}>
          Volver al listado
        </Button>
      </Box>
    );
  }

  const shareUrl = window.location.href;
  const shareTitle = evento.titulo;
  const imageUrl = evento.imagenUrl ? `${config.backendUrl}${evento.imagenUrl}` : '';

  // Datos estructurados para Google (Schema.org)
  const eventStructuredData = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": evento.titulo,
    "description": evento.descripcion,
    "image": imageUrl,
    "startDate": formatDateForGoogle(evento.fechaInicio),
    "endDate": formatDateForGoogle(evento.fechaFin),
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "eventStatus": "https://schema.org/EventScheduled",
    "location": {
      "@type": "Place",
      "name": evento.lugar,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": evento.direccion,
        "addressLocality": evento.ciudad,
        "addressRegion": evento.provincia
      }
    },
    "offers": {
      "@type": "Offer",
      "url": evento.ticketUrl || shareUrl,
      "price": evento.precio || "0",
      "priceCurrency": "ARS",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <Helmet>
        <title>{evento.titulo} | Chicos X Baires</title>
        <meta name="description" content={evento.descripcion.substring(0, 160)} />
        <meta property="og:title" content={evento.titulo} />
        <meta property="og:description" content={evento.descripcion.substring(0, 160)} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="event" />
        <script type="application/ld+json">
          {JSON.stringify(eventStructuredData)}
        </script>
      </Helmet>

      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Card>
          {evento.imagenUrl && (
            <CardMedia
              component="img"
              height="600"
              image={imageUrl}
              alt={evento.titulo}
              sx={{ objectFit: 'contain' }}
            />
          )}
          <CardContent>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {evento.titulo}
            </Typography>
            <Chip label={evento.tipo} color="primary" sx={{ mb: 2 }} />
            <Typography variant="body1" paragraph>
              {evento.descripcion}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" alignItems="center" mb={1}>
              <LocationOn sx={{ mr: 1 }} color="action" />
              <Typography variant="body2" color="text.secondary">
                {evento.lugar} - {evento.direccion}, {evento.ciudad}, {evento.provincia}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" mb={1}>
              <CalendarToday sx={{ mr: 1 }} color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatDate(evento.fechaInicio)}
                {evento.fechaFin && ` - ${formatDate(evento.fechaFin)}`}
              </Typography>
            </Box>
            {evento.precio && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                Precio: {evento.precio}
              </Typography>
            )}
            {evento.webUrl && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                Sitio web: <a href={evento.webUrl} target="_blank" rel="noopener noreferrer">{evento.webUrl}</a>
              </Typography>
            )}
            {evento.ticketUrl && (
              <Typography variant="body2" color="text.secondary">
                Tickets: <a href={evento.ticketUrl} target="_blank" rel="noopener noreferrer">{evento.ticketUrl}</a>
              </Typography>
            )}
          </CardContent>
          <CardActions sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 2, pb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Compartir este evento:
            </Typography>
            <Stack direction="row" spacing={1} mb={2}>
              <FacebookShareButton url={shareUrl} title={shareTitle}>
                <FacebookIcon size={32} round />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={shareTitle}>
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <WhatsappShareButton url={shareUrl} title={shareTitle}>
                <WhatsappIcon size={32} round />
              </WhatsappShareButton>
              <LinkedinShareButton url={shareUrl} title={shareTitle}>
                <LinkedinIcon size={32} round />
              </LinkedinShareButton>
            </Stack>

            <Typography variant="subtitle1" gutterBottom>
              Agregar al calendario:
            </Typography>
            <AddToCalendarButton
              name={evento.titulo}
              description={evento.descripcion}
              startDate={evento.fechaInicio ? new Date(evento.fechaInicio).toISOString().split('T')[0] : ''}
              endDate={evento.fechaFin ? new Date(evento.fechaFin).toISOString().split('T')[0] : ''}
              startTime={evento.fechaInicio ? new Date(evento.fechaInicio).toTimeString().slice(0, 5) : ''}
              endTime={evento.fechaFin ? new Date(evento.fechaFin).toTimeString().slice(0, 5) : ''}
              location={`${evento.lugar}, ${evento.direccion}, ${evento.ciudad}, ${evento.provincia}`}
              options={['Google', 'Apple', 'iCal', 'Outlook.com', 'Yahoo']}
              buttonStyle="round"
              size="2"
              lightMode="system"
            />
          </CardActions>
        </Card>

        <Box mt={3} textAlign="center">
          <Button component={Link} to="/eventos" variant="contained">
            Volver al listado
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default EventoDetalle;